import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  ShieldCheck, Loader2, AlertTriangle, CheckCircle2, XCircle,
  Award, RefreshCw, Send, FileSignature, Building2, Download, QrCode, Copy, ExternalLink,
  Mail, Link2
} from 'lucide-react';
import api from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

export default function AuditPage() {
  const navigate = useNavigate();
  const [auditResult, setAuditResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [docuSignForm, setDocuSignForm] = useState({
    signerEmail: '',
    signerName: ''
  });
  const [sendingDoc, setSendingDoc] = useState(false);
  const [entities, setEntities] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [anchoring, setAnchoring] = useState(false);
  const [anchorResult, setAnchorResult] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    loadHistory();
    loadEntities();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await api.get('/audit/history');
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to load audit history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadEntities = async () => {
    try {
      const res = await api.get('/entities');
      setEntities(res.data);
    } catch (err) {
      console.error('Failed to load entities:', err);
    }
  };

  const runAudit = async () => {
    setLoading(true);
    try {
      const res = await api.post('/audit/structure');
      setAuditResult(res.data);
      loadHistory();
    } catch (err) {
      console.error('Audit failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendForSignature = async () => {
    if (!docuSignForm.signerEmail || !docuSignForm.signerName) {
      toast.error('Please enter signer name and email');
      return;
    }
    if (entities.length === 0) {
      toast.error('You have no entities yet. Create one before sending for signature.');
      return;
    }

    setSendingDoc(true);
    try {
      // Send a real email signing link via Resend for each entity.
      // (Replaces the old mock /docusign/send endpoint — that one never
      // actually delivered an email since DocuSign OAuth is not configured.)
      const results = await Promise.allSettled(
        entities.map((e) =>
          api.post('/signatures/create-link', {
            entity_id: e.id,
            document_name: `${e.name} — Formation Documents`,
            document_type: 'formation',
            signer_email: docuSignForm.signerEmail,
            signer_name: docuSignForm.signerName,
          }),
        ),
      );

      const sent = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - sent;

      if (sent > 0) {
        toast.success(
          `${sent} signing link${sent === 1 ? '' : 's'} emailed to ${docuSignForm.signerEmail}`,
          {
            description: failed > 0
              ? `${failed} failed — check console`
              : 'They expire in 7 days.',
            duration: 6000,
          },
        );
        setDocuSignForm({ signerEmail: '', signerName: '' });
      } else {
        const firstErr = results[0]?.reason?.response?.data?.detail || 'Failed to send';
        toast.error(`Could not send signing links: ${firstErr}`);
      }
    } catch (err) {
      console.error('Failed to send for signature:', err);
      toast.error(err.response?.data?.detail || 'Failed to send for signature');
    } finally {
      setSendingDoc(false);
    }
  };

  const downloadPdf = async (certificateId) => {
    setDownloadingPdf(true);
    try {
      const response = await api.get(`/certificate/pdf/${certificateId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `corpcraft_certificate_${certificateId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download certificate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const copyShareLink = (certificateId) => {
    const link = `${window.location.origin}/verify/${certificateId}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const anchorToBlockchain = async (certificateId) => {
    setAnchoring(true);
    setAnchorResult(null);
    try {
      const res = await api.post(`/blockchain/anchor/${certificateId}`);
      setAnchorResult(res.data);
      loadHistory(); // Refresh to show anchor status
    } catch (err) {
      console.error('Blockchain anchor failed:', err);
      setAnchorResult({ success: false, error: err.message });
    } finally {
      setAnchoring(false);
    }
  };

  const sendAuditEmail = async () => {
    setSendingEmail(true);
    try {
      await api.post('/notifications/send-audit-results', {});
      alert('Audit results sent to your email!');
    } catch (err) {
      console.error('Failed to send email:', err);
      alert('Failed to send email. Please check your email settings.');
    } finally {
      setSendingEmail(false);
    }
  };

  const getGradeColor = (grade) => {
    const colors = {
      A: 'text-emerald-400 bg-emerald-400/20',
      B: 'text-blue-400 bg-blue-400/20',
      C: 'text-amber-400 bg-amber-400/20',
      D: 'text-orange-400 bg-orange-400/20',
      F: 'text-red-400 bg-red-400/20'
    };
    return colors[grade] || colors.C;
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="audit-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-['Outfit'] flex items-center gap-3">
              <ShieldCheck className="w-8 h-8 text-emerald-400" />
              Structure Audit & Certification
            </h1>
            <p className="text-slate-400 mt-1">Audit your corporate structure for compliance and best practices</p>
          </div>
          <Button
            onClick={runAudit}
            disabled={loading}
            className="gradient-accent hover:opacity-90"
            data-testid="run-audit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <RefreshCw className="w-5 h-5 mr-2" />
                Run Audit
              </>
            )}
          </Button>
        </div>

        {/* Audit Result */}
        {auditResult && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="audit-result">
            {/* Score Card */}
            <div className="stat-card text-center">
              <div className={`w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center ${getGradeColor(auditResult.audit.grade)}`}>
                <span className="text-4xl font-bold">{auditResult.audit.grade}</span>
              </div>
              <p className="text-3xl font-bold mb-2">{auditResult.audit.score}/100</p>
              <p className="text-slate-400">Structure Score</p>
              <p className="text-sm text-slate-500 mt-2">{auditResult.entities_audited} entities audited</p>
            </div>

            {/* Certification Status */}
            <div className="stat-card">
              <div className="flex items-center gap-3 mb-4">
                <Award className={`w-6 h-6 ${auditResult.certification.status === 'CorpCraft Certified' ? 'text-amber-400' : 'text-slate-500'}`} />
                <h3 className="font-semibold">Certification</h3>
              </div>
              
              {auditResult.certification.status === 'CorpCraft Certified' ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-emerald-400">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">{auditResult.certification.status}</span>
                  </div>
                  <p className="text-sm text-slate-400">Level: {auditResult.certification.level}</p>
                  <code className="text-xs bg-slate-800 px-3 py-2 rounded block">
                    {auditResult.certification.certificate_id}
                  </code>
                  
                  {/* Certificate Actions */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={() => downloadPdf(auditResult.certification.certificate_id)}
                      disabled={downloadingPdf}
                      className="bg-slate-800 hover:bg-slate-700"
                      data-testid="download-pdf-btn"
                    >
                      {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                      PDF
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => copyShareLink(auditResult.certification.certificate_id)}
                      className="bg-slate-800 hover:bg-slate-700"
                      data-testid="copy-link-btn"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      {copiedLink ? 'Copied!' : 'Copy Link'}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => window.open(`/verify/${auditResult.certification.certificate_id}`, '_blank')}
                      className="bg-slate-800 hover:bg-slate-700"
                      data-testid="verify-public-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Verify
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-red-400">
                    <XCircle className="w-5 h-5" />
                    <span className="font-medium">{auditResult.certification.status}</span>
                  </div>
                  <p className="text-sm text-slate-400">{auditResult.certification.reason}</p>
                </div>
              )}
            </div>

            {/* QR Code for Certified Structures */}
            {auditResult.certification.status === 'CorpCraft Certified' && (
              <div className="stat-card text-center">
                <QrCode className="w-6 h-6 text-blue-400 mx-auto mb-3" />
                <h3 className="font-semibold mb-4">Scan to Verify</h3>
                <div className="inline-block bg-white p-3 rounded-xl">
                  <QRCodeSVG 
                    value={`${window.location.origin}/verify/${auditResult.certification.certificate_id}`}
                    size={120}
                    level="H"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Share this QR code to allow instant verification
                </p>
                
                {/* Blockchain Anchoring */}
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <Button
                    onClick={() => anchorToBlockchain(auditResult.certification.certificate_id)}
                    disabled={anchoring}
                    className="w-full bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30"
                    data-testid="anchor-blockchain-btn"
                  >
                    {anchoring ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Link2 className="w-4 h-4 mr-2" />
                    )}
                    Anchor to Ethereum
                  </Button>
                  {anchorResult && (
                    <div className={`mt-3 text-sm ${anchorResult.success ? 'text-emerald-400' : 'text-red-400'}`}>
                      {anchorResult.success ? (
                        <a
                          href={anchorResult.explorer_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-1 hover:underline"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Anchored! View TX
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span>{anchorResult.error || 'Anchoring failed'}</span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Email Results */}
                <div className="mt-3">
                  <Button
                    onClick={sendAuditEmail}
                    disabled={sendingEmail}
                    variant="ghost"
                    className="w-full text-slate-400 hover:text-white"
                  >
                    {sendingEmail ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    Email Results
                  </Button>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="stat-card">
              <h3 className="font-semibold mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/entities/new')}
                  className="w-full justify-start bg-slate-800 hover:bg-slate-700"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Add Entity
                </Button>
                <Button
                  onClick={() => navigate('/ai-analysis')}
                  className="w-full justify-start bg-slate-800 hover:bg-slate-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Get AI Recommendation
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Risks & Recommendations */}
        {auditResult && auditResult.audit.risks.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risks */}
            <div className="stat-card">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Risk Factors
              </h3>
              <div className="space-y-3">
                {auditResult.audit.risks.map((risk) => (
                  <div key={`${risk.type}-${risk.message.slice(0, 20)}`} className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                    <div className={`px-2 py-1 rounded text-xs font-medium capitalize ${
                      risk.type === 'legal' ? 'bg-red-500/20 text-red-400' :
                      risk.type === 'structure' ? 'bg-purple-500/20 text-purple-400' :
                      risk.type === 'asset_protection' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {risk.type}
                    </div>
                    <p className="text-sm text-slate-300">{risk.message}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendations */}
            <div className="stat-card">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                Recommendations
              </h3>
              <div className="space-y-3">
                {auditResult.audit.recommendations.map((rec, i) => (
                  <div key={`rec-${i}-${rec.slice(0, 30)}`} className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                      <span className="text-xs text-emerald-400 font-medium">{i + 1}</span>
                    </div>
                    <p className="text-sm text-slate-300">{rec}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* DocuSign Integration */}
        {entities.length > 0 && (
          <div className="stat-card" data-testid="docusign-section">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-blue-400" />
              Email Signing Link
            </h3>
            <p className="text-sm text-slate-400 mb-4">
              We'll email a secure signing link to your signer for each of your {entities.length} {entities.length === 1 ? 'entity' : 'entities'}. Links expire in 7 days.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Signer Name</Label>
                <Input
                  value={docuSignForm.signerName}
                  onChange={(e) => setDocuSignForm({ ...docuSignForm, signerName: e.target.value })}
                  placeholder="John Smith"
                  className="bg-slate-900 border-slate-800"
                  data-testid="signer-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Signer Email</Label>
                <Input
                  type="email"
                  value={docuSignForm.signerEmail}
                  onChange={(e) => setDocuSignForm({ ...docuSignForm, signerEmail: e.target.value })}
                  placeholder="signer@company.com"
                  className="bg-slate-900 border-slate-800"
                  data-testid="signer-email-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={sendForSignature}
                  disabled={sendingDoc || !docuSignForm.signerEmail || !docuSignForm.signerName}
                  className="w-full gradient-accent hover:opacity-90"
                  data-testid="send-docusign-btn"
                >
                  {sendingDoc ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Signing Link
                    </>
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Powered by Resend · Emails sent from {`no-reply@corpcraftlegal.org`} · Signer clicks the link to review &amp; e-sign.
            </p>
          </div>
        )}

        {/* Audit History */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Audit History</h2>
          {loadingHistory ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : history.length === 0 ? (
            <div className="stat-card text-center py-12" data-testid="no-audit-history">
              <ShieldCheck className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No audits yet</h3>
              <p className="text-slate-400">Run your first audit to see results here</p>
            </div>
          ) : (
            <div className="stat-card overflow-hidden p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Score</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Grade</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Certification</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase">Entities</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {history.map((audit) => (
                    <tr key={audit.id} className="hover:bg-slate-800/30">
                      <td className="px-6 py-4 text-sm">{formatDate(audit.created_at)}</td>
                      <td className="px-6 py-4 text-sm font-medium">{audit.audit_result?.score}/100</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getGradeColor(audit.audit_result?.grade)}`}>
                          {audit.audit_result?.grade}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {audit.certification?.status === 'CorpCraft Certified' ? (
                          <span className="text-emerald-400">Certified</span>
                        ) : (
                          <span className="text-slate-500">Not Certified</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-400">{audit.entity_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
